import Base from '@studiometa/js-toolkit';
import gsap from 'gsap';
import random from 'canvas-sketch-util/random';
import getSize from '../utils/getSize.js';

let PIXI = null;

/**
 * CanvasDeformation
 */
export default class Canvas extends Base {
  /**
   * Config
   */
  get config() {
    return {
      name: 'Canvas',
    };
  }

  /**
   * Import PIXI and init PIXI
   */
  async mounted() {
    this.dpr = window.devicePixelRatio && window.devicePixelRatio >= 2 ? 2 : 1;
    this.sprites = [];
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.texture = '/wp-content/themes/studiometa/static/img/texture.jpg';
    this.displacementSprite = null;
    this.index = 0;

    PIXI = await import(/* webpackChunkName: "pixi" */ 'pixi.js');

    if (!PIXI.utils.isWebGLSupported()) {
      return this.setFallback();
    }

    this.init();

    return this;
  }

  /**
   * Init PIXI
   */
  init() {
    this.setPixiSettings();
    this.getImages();
    this.loadImages();
    this.createPixiApp();
  }

  /**
   * Resized service hook
   * @see https://js-toolkit.meta.fr/services/resize.html
   * @param {number} width The width of the viewport.
   * @param {number} height The height of the viewport.
   * @param {number} ratio The width / height ratio of the viewport.
   * @param {string} orientation square, portrait or landscape
   * @param {string} breakpoint The current active breakpoint extracted from the content value of the ::before pseudo-element on the first [data-breakpoint] element found in the DOM.
   */
  resized({ width, height }) {
    // Resize the PIXI renderer (it will automaticcaly fit screen because of the resizeTo param used in createPixiApp)
    if (this.app) {
      this.app.renderer.resize(width, height);
    }

    // Redraw all sprites to fit new screen size
    if (this.sprites.length > 0) {
      this.sprites.forEach((sprite) => {
        this.setSpriteSize(sprite);
      });
    }
  }

  /**
   * Set PIXI Global Settings
   */
  setPixiSettings() {
    PIXI.utils.skipHello();

    if (navigator.deviceMemory > 1) {
      PIXI.settings.SCALE_MODE = PIXI.SCALE_MODES.LINEAR;
      PIXI.settings.PRECISION_FRAGMENT = PIXI.PRECISION.HIGH;
    }
  }

  /**
   * Create PIXI App and append it to the DOM
   */
  createPixiApp() {
    this.app = new PIXI.Application(this.width, this.height, {
      width: this.width,
      height: this.height,
      transparent: true,
      resolution: this.dpr,
      autoResize: true,
      resizeTo: window,
    });
    this.app.renderer.resize(this.width, this.height);
    this.$el.append(this.app.view);
  }

  /**
   * Get Images URL from HTML attribute
   */
  getImages() {
    this.images = {};

    this.$parent.$refs.images.forEach((item, i) => {
      const image = item.dataset.img;
      this.images[i] = { image };
    });

    return this.images;
  }

  /**
   * Load Images
   */
  loadImages() {
    const loader = new PIXI.Loader();
    Object.keys(this.images).forEach((key) => {
      loader.add(key, this.images[key].image);
    });

    loader.load((l, images) => {
      this.images = images;
    });

    // Create PIXI Scene after all images are loaded
    loader.onComplete.add(() => {
      this.createScene();
    });
  }

  /**
   * Create PIXI Scene
   */
  createScene() {
    this.container = new PIXI.Container();
    this.app.stage.addChild(this.container);
    this.app.stage.interactive = false;
    this.app.stage.interactiveChildren = false;
    this.container.width = this.width;
    this.container.height = this.height;

    this.addSprites();
    this.addDisplacementSprite();
    this.addDisplacementFilter();

    // Everything is setup run animation to show the first sprite
    this.afterLoadAnimation();
  }

  /**
   * Reset slides
   */
  setSpriteSize(sprite) {
    // Get a size for the slide that mimic background-size: cover
    const slideSize = getSize(
      'cover',
      sprite.width,
      sprite.height,
      window.innerWidth,
      window.innerHeight
    );

    sprite.width = slideSize.width;
    sprite.height = slideSize.height;

    // Center the slide
    sprite.anchor.set(0.5);
    sprite.position.x = window.innerWidth / 2;
    sprite.position.y = window.innerHeight / 2;
  }

  /**
   * Add the images to PIXI container
   */
  addSprites() {
    Object.keys(this.images).forEach((key, i) => {
      const sprite = new PIXI.Sprite(this.images[key].texture);
      this.setSpriteSize(sprite);

      // sprite is invisible by default
      sprite.alpha = 0;
      this.sprites[i] = sprite;
      this.container.addChild(sprite);
    });
  }

  /**
   * Create a displacementSprite from a texture
   */
  addDisplacementSprite() {
    this.displacementSprite = PIXI.Sprite.from(this.texture);
    this.displacementSprite.texture.baseTexture.wrapMode = PIXI.WRAP_MODES.REPEAT;
    this.displacementSprite.skew.x = 1;
    this.displacementSprite.skew.y = -1;
    this.container.addChild(this.displacementSprite);
  }

  /**
   * Add Displacement Filter
   */
  addDisplacementFilter() {
    this.displacementFilter = new PIXI.filters.DisplacementFilter(this.displacementSprite);
    this.displacementFilter.autoFit = true;
    this.displacementFilter.scale.x = 0;
    this.displacementFilter.scale.y = 0;
    this.container.filters = [this.displacementFilter];
  }

  /**
   * After Load animation
   */
  afterLoadAnimation() {
    const tl = gsap.timeline();
    tl.fromTo(
      this.displacementFilter.scale,
      { x: 600, y: 600 },
      { x: 0, y: 0, duration: 1.8, ease: 'power1.out' },
      0.9
    );
    tl.to(this.displacementSprite, { x: 0, y: 0, duration: 1.8, ease: 'power1.out' }, 0.6);
    tl.to(this.sprites[0], { alpha: 1, duration: 0.8, ease: 'sine.in' }, 0.6);
  }

  /**
   * Slide change Animation
   */
  changeSlideAnimation(oldIndex, newIndex) {
    // Get Random Values for the displacementSprite
    // in order to have unique animations at each change
    const randomX = random.range(-200, 200);
    const randomY = random.range(-200, 200);

    // Create a Timeline for the animation
    const tl = gsap.timeline();

    // Make sure new slide is hidden
    tl.set(this.sprites[newIndex], { alpha: 0 }, 0);

    // Fade out the old slide
    tl.to(this.sprites[oldIndex], { alpha: 0, duration: 0.9, ease: 'power1.out' }, 0.45);

    // Distord the sprite and the filter
    tl.to(this.displacementFilter.scale, { x: 2000, y: 5000, duration: 1, ease: 'power3.in' }, 0);
    tl.to(this.displacementSprite, { x: randomX, y: randomY, duration: 1, ease: 'power2.out' }, 0);

    // Reset the distortions
    tl.to(this.displacementFilter.scale, { x: 0, y: 0, duration: 1.2, ease: 'power1.out' }, 1);
    tl.to(this.displacementSprite, { x: 0, y: 0, duration: 1.2, ease: 'sine.out' }, 1);

    // Make new slide visible
    tl.fromTo(
      this.sprites[newIndex],
      { alpha: 0 },
      { alpha: 1, duration: 0.8, ease: 'sine.out' },
      1.2
    );

    this.index = newIndex;
  }

  /**
   * @todo
   */
  setFallback() {}
}
